import useAttachAzureSaasToOrganization from "./useAttachAzureSaasToOrganization";
import AzureSso from "./AzureSso";
import React, { PropsWithChildren, useContext } from "react";
import { useHistory } from "react-router";
import useLoginWithAzure from "./useLoginWithAzure";
import { useFullApi } from "../../../core/api/Api";
import { OrganizationSelectList } from "../../UserOrganizations";
import { observer } from "mobx-react-lite";
import styles from "./azure.module.scss";
import { GlobalContext } from "../../../GlobalContext";
import { LoginContext } from "../provider/Context";
import { DisabledAndLoadingContext, useDisabledAndLoading } from "../../../core/context/DisabledAndLoading";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { LoginReply } from "../../../core/api/contracts/login";
import { useLoginStateProvider } from "../../../LoginStateProvider";
import { useLoginEventTrackers } from "../provider/useLoginEventTrackers";

/**
 * This login provider handles validating the marketplace azure landing page flow.
 * Azure SSO (get user and access token) -> handle org/no org/multi org -> attach saas to selected org -> redirect to homepage
 * @type {React.FunctionComponent<React.PropsWithChildren<{}>>}
 */
const LoginProvider = observer((props: PropsWithChildren<{}>) => {
  const context = useContext(GlobalContext);
  const history = useHistory();
  const disabledAndLoading = useDisabledAndLoading();
  const attach = useAttachAzureSaasToOrganization();
  const loginStateProvider = useLoginStateProvider();
  const tracker = useLoginEventTrackers();

  const attachAndRedirect = () => {
    attach(context.api.authenticationResult)
      .then(() => {
        tracker.trackLogin();
        history.push("/");
      })
      .finally(() => {
        context.auth.authentication.loading = false;
        context.auth.authentication.authenticated = true;
        disabledAndLoading.setLoading(false);
      });
  };

  const onSuccess = (loginReply: LoginReply) => {
    tracker.trackLogin();
    if (loginReply.organizations.length > 1) {
      context.auth.authentication.loading = false;
      context.auth.authentication.authenticated = true;
    } else if (loginReply.organizations.length === 1) {
      disabledAndLoading.setLoading(true);
      loginStateProvider.handleLoginReply(loginReply).then(() => {
        attachAndRedirect();
      });
    }
  };

  if (context.api.multiOrgs && context.api.authenticationResult) {
    return (
      <Container maxWidth="sm">
        <Box p={1}>
          <div className={styles.SaasOrgSelect}>
            Looks like your account is connected to multiple organizations. Select an organization to connect with the
            Azure marketplace from the list below.
            <OrganizationSelectList
              organizations={context.api.multiOrgs}
              gotoDefault={false}
              hasDefault={false}
              onSelect={() => {
                attachAndRedirect();
              }}
            />
          </div>
        </Box>
      </Container>
    );
  }

  return (
    <DisabledAndLoadingContext.Provider value={disabledAndLoading}>
      <LoginContext.Provider value={{ onLoginSuccess: onSuccess }}>{props.children}</LoginContext.Provider>
    </DisabledAndLoadingContext.Provider>
  );
});

function Button() {
  const api = useFullApi();
  const handleAuthenticationMethod = useLoginWithAzure();

  return (
    <AzureSso
      onAuthenticated={(result) => {
        // we need to save the result in an outside context
        api.authenticationResult = result;
        return handleAuthenticationMethod(result);
      }}
    />
  );
}

/**
 * Button for azure based marketplace flow
 * @returns {JSX.Element}
 * @constructor
 */
const AzureSaasAttach = observer(() => {
  return (
    <LoginProvider>
      <h5>Sign in to connect your Upsolver account to Azure Marketplace</h5>
      <Button />
    </LoginProvider>
  );
});

export default AzureSaasAttach;
